/**
 * UserAccessProvider — app-wide source of the current user's tier & capabilities.
 *
 * On mount it asks the server to (re)evaluate the user's tier and returns the
 * authoritative `capabilities` list. Any component can then check access via the
 * context's `can(capability)` — including <TierRoute> for route-level guarding, so
 * we can verify permission at any point in the journey (e.g. deep links).
 *
 * Enforcement lives on the server; this only mirrors the computed result for the UI.
 */
import React from 'react';
import { clientURI } from '../api/clientURI';
import { USER_EVALUATE_TIER } from '../api/graphql-mutation';
import { getUserId } from '../utility/function';
import { can as canCapability } from '../utility/permissions';
import session from '../model/session';
import history from '../utility/history';

export const UserAccessContext = React.createContext({
    ready: false,
    isLoggedIn: false,
    tier: 0,
    capabilities: [],
    criteria: [],
    can: () => false,
    refresh: () => {},
});

export class UserAccessProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            ready: false,
            isLoggedIn: false,
            tier: 0,
            capabilities: [],
            criteria: [],
        };
    }

    componentDidMount() {
        this.load();
        // Login/registration navigate via history.push (SPA, no full reload), so the
        // provider must re-evaluate when the session's user changes mid-session —
        // otherwise it stays "logged out" and TierRoutes keep bouncing to /login.
        this.unlisten = history.listen(() => this.syncOnNavigation());
    }

    componentWillUnmount() {
        if (this.unlisten) this.unlisten();
    }

    // On SPA navigation, reload if the logged-in user changed (login, logout, or switch).
    syncOnNavigation = () => {
        let uid = null;
        try { uid = getUserId(); } catch (e) { uid = null; }
        uid = uid || null;
        if (uid !== this.loadedUid) {
            this.load();
        }
    };

    load = async () => {
        // The session loads from IndexedDB asynchronously; getUserId() reads it
        // synchronously, so on a fresh page load (deep link / full navigation) it would
        // return undefined and we'd wrongly treat the user as logged out (→ redirect).
        // Wait for the session before deciding.
        try {
            await session.fetchLocal();
        } catch (e) {
            // ignore — fall through and let getUserId() report what it can
        }

        let uid = null;
        try {
            uid = getUserId();
        } catch (e) {
            uid = null;
        }
        this.loadedUid = uid || null; // remember who we evaluated for (see syncOnNavigation)

        if (!uid) {
            // Unregistered / logged-out: no capabilities (tier 0).
            this.setState({ ready: true, isLoggedIn: false, tier: 0, capabilities: [], criteria: [] });
            return;
        }

        clientURI.mutate({
            mutation: USER_EVALUATE_TIER,
            variables: { _id: uid },
            fetchPolicy: 'no-cache',
        }).then((res) => {
            const u = (res && res.data && res.data.userEvaluateTier) || {};
            this.setState({
                ready: true,
                isLoggedIn: true,
                tier: parseInt(u.access, 10) || 0,
                capabilities: u.capabilities || [],
                criteria: u.satisfiedCriteria || [],
            });
        }).catch((err) => {
            console.log('UserAccessProvider load failed:', err);
            // Fail closed on capabilities, but remember they are logged in.
            this.setState({ ready: true, isLoggedIn: true, tier: 0, capabilities: [], criteria: [] });
        });
    };

    can = (capability) => canCapability(capability, this.state.capabilities);

    render() {
        return (
            <UserAccessContext.Provider
                value={{
                    ready: this.state.ready,
                    isLoggedIn: this.state.isLoggedIn,
                    tier: this.state.tier,
                    capabilities: this.state.capabilities,
                    criteria: this.state.criteria,
                    can: this.can,
                    refresh: this.load,
                }}
            >
                {this.props.children}
            </UserAccessContext.Provider>
        );
    }
}

export default UserAccessProvider;
